// import { useRouter } from "next/router";
import { Button } from "primereact/button";
import { Ripple } from "primereact/ripple";
import { classNames } from "primereact/utils";
import React, { useContext, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../App";
import type { AppMenuItemProps } from "../types/types";
import { LayoutContext } from "./context/layoutcontext";
import { MenuContext } from "./context/menucontext";
import { useSubmenuOverlayPosition } from "./hooks/useSubmenuOverlayPosition";

const AppMenuitem = (props: AppMenuItemProps) => {
  const navigate = useNavigate();
  const { activeMenu, setActiveMenu } = useContext(MenuContext);
  const { currentEvent } = useContext(AppContext);
  const {
    isSlim,
    isSlimPlus,
    isHorizontal,
    isDesktop,
    setLayoutState,
    layoutState,
    layoutConfig,
  } = useContext(LayoutContext);
  const href = window.location.pathname;
  //   const router = useRouter();
  const submenuRef = useRef<HTMLUListElement>(null);
  const menuitemRef = useRef<HTMLLIElement>(null);
  const item = props.item;
  const key = props.parentKey
    ? props.parentKey + "-" + props.index
    : String(props.index);
  //  check also if one the child items is active and don't consider :id
  const isActiveRoute =
    item!.to &&
    (href === item!.to ||
      item?.items?.some(
        //don't consider :id
        (child) =>
          child.to &&
          child.makeParentActive &&
          href.startsWith(child.to.split(":")[0] ?? child.to),
      ));
  const active =
    activeMenu === key || !!(activeMenu && activeMenu.startsWith(key + "-"));

  useSubmenuOverlayPosition({
    target: menuitemRef.current,
    overlay: submenuRef.current,
    container:
      menuitemRef.current &&
      menuitemRef.current.closest(".layout-menu-container"),
    when:
      props.root &&
      active &&
      (isSlim() || isSlimPlus() || isHorizontal()) &&
      isDesktop(),
  });

  useEffect(() => {
    if (layoutState.resetMenu) {
      setActiveMenu("");
      setLayoutState((prevLayoutState) => ({
        ...prevLayoutState,
        resetMenu: false,
      }));
    }
  }, [layoutState, setActiveMenu, setLayoutState]);

  useEffect(() => {
    if (!(isSlim() || isSlimPlus() || isHorizontal()) && isActiveRoute) {
      setActiveMenu(key);
    }

    // const onRouteChange = (url: string) => {
    //   if (!(isSlim() || isHorizontal()) && item!.to && item!.to === url) {
    //     setActiveMenu(key);
    //   }
    // };

    // router.events.on("routeChangeComplete", onRouteChange);
    // return () => {
    //   router.events.off("routeChangeComplete", onRouteChange);
    // };
    //   }, [router, layoutConfig]);
  }, [
    isActiveRoute,
    isHorizontal,
    isSlim,
    isSlimPlus,
    key,
    layoutConfig,
    setActiveMenu,
  ]);

  const itemClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    //avoid processing disabled items
    if (item!.disabled) {
      event.preventDefault();
      return;
    }

    // navigate with hover
    if (props.root && (isSlim() || isHorizontal() || isSlimPlus())) {
      const isSubmenu =
        event.currentTarget.closest(
          ".layout-root-menuitem.active-menuitem > ul",
        ) !== null;
      if (isSubmenu)
        setLayoutState((prevLayoutState) => ({
          ...prevLayoutState,
          menuHoverActive: true,
        }));
      else
        setLayoutState((prevLayoutState) => ({
          ...prevLayoutState,
          menuHoverActive: !prevLayoutState.menuHoverActive,
        }));
    }

    //execute command
    if (item?.command) {
      item?.command({ originalEvent: event, item: item });
    }

    // toggle active state
    if (item?.items) {
      setActiveMenu(active ? props.parentKey! : key);

      if (
        props.root &&
        !active &&
        (isSlim() || isHorizontal() || isSlimPlus())
      ) {
        setLayoutState((prevLayoutState) => ({
          ...prevLayoutState,
          overlaySubmenuActive: true,
        }));
      }
    } else {
      if (!isDesktop()) {
        setLayoutState((prevLayoutState) => ({
          ...prevLayoutState,
          staticMenuMobileActive: !prevLayoutState.staticMenuMobileActive,
        }));
      }

      if (isSlim() || isSlimPlus() || isHorizontal()) {
        setLayoutState((prevLayoutState) => ({
          ...prevLayoutState,
          menuHoverActive: false,
        }));
      }

      setActiveMenu(key);
    }
  };

  const onMouseEnter = () => {
    // activate item on hover
    if (
      props.root &&
      (isSlim() || isHorizontal() || isSlimPlus()) &&
      isDesktop()
    ) {
      if (!active && layoutState.menuHoverActive) {
        setActiveMenu(key);
      }
    }
  };

  const badge = item?.badge ? (
    <span
      className={classNames(
        "layout-menu-badge p-tag p-tag-rounded ml-2 uppercase",
        {
          [`${item?.badge}`]: true,
          "p-tag-success": item?.badge === "new",
          "p-tag-info": item?.badge === "updated",
        },
      )}
    >
      {item?.badge}
    </span>
  ) : null;
  const subMenu =
    item?.items && item?.visible !== false ? (
      <ul ref={submenuRef}>
        {item?.items.map((child, i) => {
          return (
            <AppMenuitem
              item={child}
              index={i}
              className={child.badgeClass}
              parentKey={key}
              key={child.label}
            />
          );
        })}
      </ul>
    ) : null;

  const isEventDetailHeader = useMemo<boolean>(
    () =>
      !!(
        props.root &&
        props.index === 0 &&
        item?.visible !== false &&
        currentEvent?.id
      ),
    [props.root, props.index, item?.visible, currentEvent?.id],
  );

  // const formik = useFormik({
  //   initialValues: {
  //     name: "",
  //     description: "",
  //     place_id: "",
  //     date: "",
  //     status: EventStatus.programmed,
  //   },
  //   validate: (data) => {
  //     let errors: Error = {};
  //
  //     if (!data.name) {
  //       errors.name = "Name is required.";
  //     }
  //
  //     if (!data.place_id) {
  //       errors.place_id = "Place is required.";
  //     }
  //
  //     if (!data.date) {
  //       errors.date = "Date is required.";
  //     }
  //
  //     if (!data.status) {
  //       errors.status = "Status is required.";
  //     }
  //
  //     return errors;
  //   },
  //   onSubmit: (data) => {
  //     mUpdateEvent.mutate({ id: event_id!, ...data });
  //   },
  // });

  return (
    <li
      ref={menuitemRef}
      className={classNames({
        "layout-root-menuitem": props.root,
        "active-menuitem": active,
      })}
    >
      {isEventDetailHeader && (
        <div className="flex align-items-center pl-3 layout-root-menuitem">
          <Button
            icon="pi pi-arrow-left"
            text
            severity="info"
            onClick={() => navigate("/events")}
          />
          <div className="layout-menuitem-root-text pl-0">{item?.label}</div>
        </div>
      )}
      {!isEventDetailHeader && props.root && item?.visible !== false && (
        <div className="layout-menuitem-root-text">{item?.label}</div>
      )}
      {!item?.to && item?.items && item?.visible !== false ? (
        <>
          <a
            href={item?.url}
            onClick={(e) => itemClick(e)}
            className={classNames(item?.class, "p-ripple tooltip-target")}
            target={item?.target}
            data-pr-tooltip={item?.label}
            data-pr-disabled={
              !(isSlim() && props.root && !layoutState.menuHoverActive)
            }
            tabIndex={0}
            onMouseEnter={onMouseEnter}
          >
            <i className={classNames("layout-menuitem-icon", item?.icon)}></i>
            <span className="layout-menuitem-text">{item?.label}</span>
            {item?.items && (
              <i className="pi pi-fw pi-angle-down layout-submenu-toggler"></i>
            )}
            <Ripple />
          </a>
        </>
      ) : null}
      {item?.to && item?.visible !== false ? (
        <>
          <a
            href={item?.to}
            // replace={item?.replaceUrl}
            onClick={(e: any) => {
              e.preventDefault();
              itemClick(e);
              navigate(item?.to ?? "");
            }}
            className={classNames(item?.class, "p-ripple ", {
              "active-route": isActiveRoute,
            })}
            tabIndex={0}
            onMouseEnter={onMouseEnter}
          >
            <i className={classNames("layout-menuitem-icon", item?.icon)}></i>
            <span className="layout-menuitem-text">{item?.label}</span>
            {badge}
            {/*{item?.items && (*/}
            {/*    <i className="pi pi-fw pi-angle-down layout-submenu-toggler"></i>*/}
            {/*)}*/}
            <Ripple />
          </a>
        </>
      ) : null}
      {subMenu}
    </li>
  );
};

export default AppMenuitem;
